import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function RegulationsPage() {
  const { t } = useTranslation();

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const checkpoints = {
    mini: [
      { km: '4.0', location: 'Katun Rabrenovića (1465m)', cutoff: '1h 30min', aid: true },
      { km: '6.2', location: 'Prevoj (1734m)', cutoff: '2h 15min', aid: false },
      { km: '10.0', location: 'Zabojsko jezero, Eko koliba (1525m)', cutoff: '3h 00min', aid: false, finish: true },
    ],
    skyrace: [
      { km: '4.0', location: 'Katun Rabrenovića (1465m)', cutoff: '1h 30min', aid: true },
      { km: '6.2', location: 'Prevoj (1734m)', cutoff: '2h 15min', aid: false },
      { km: '10.0', location: 'Zabojsko jezero, Eko koliba (1525m)', cutoff: '3h 00min', aid: true },
      { km: '21.3', location: 'Katun Okrugljak, Crkva Ružica (1725m)', cutoff: '5h 15min', aid: true },
      { km: '31.3', location: 'Planinarski dom Ckara (1636m)', cutoff: '6h 45min', aid: true },
      { km: '36.0', location: 'Topovi (1210m)', cutoff: '', aid: true },
      { km: '40.0', location: 'Gradski trg Mojkovac (820m)', cutoff: '8h 00min', aid: false, finish: true },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/0d2a8bbfaad65247a8e821bed673477a/276e968c69d27d90184bde4c23c5bae9.jpeg"
              alt="Race Regulations"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-black/60"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-futura font-black text-5xl sm:text-6xl md:text-7xl mb-6"
            >
              {t('regulations:hero.title')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-white/90"
            >
              {t('regulations:hero.subtitle')}
            </motion.p>
          </div>
        </section>

        {/* Race Overview */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp}>
              <h2 className="font-futura font-black text-4xl text-primary border-l-8 border-primary pl-6 mb-8">
                {t('regulations:overview.title')}
              </h2>
              <p className="font-futura font-bold text-xl text-gray-900 mb-6">
                {t('regulations:overview.subtitle')}
              </p>
              <ul className="space-y-3 mb-10">
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-primary text-xl mt-0.5"></i>
                  <span className="text-base">{t('regulations:overview.race1')}</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-primary text-xl mt-0.5"></i>
                  <span className="text-base">{t('regulations:overview.race2')}</span>
                </li>
              </ul>

              {/* Info Table */}
              <div className="overflow-x-auto rounded-xl shadow-lg">
                <table className="w-full">
                  <thead>
                    <tr className="bg-primary text-white">
                      <th className="px-6 py-4 text-left font-futura font-bold">
                        {t('regulations:overview.tableHeaders.detail')}
                      </th>
                      <th className="px-6 py-4 text-left font-futura font-bold">
                        {t('regulations:overview.tableHeaders.information')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.startLocation')}</td>
                      <td className="px-6 py-4 text-gray-700">{t('regulations:overview.startLocationValue')}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.startCoordinates')}</td>
                      <td className="px-6 py-4 text-gray-700">42.989524, 19.410788</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.miniFinish')}</td>
                      <td className="px-6 py-4 text-gray-700">{t('regulations:overview.miniFinishValue')}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.miniFinishCoords')}</td>
                      <td className="px-6 py-4 text-gray-700">43.012500, 19.373071</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.skyraceFinish')}</td>
                      <td className="px-6 py-4 text-gray-700">{t('regulations:overview.skyraceFinishValue')}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.skyraceFinishCoords')}</td>
                      <td className="px-6 py-4 text-gray-700">42.962126, 19.579286</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.miniMaxAlt')}</td>
                      <td className="px-6 py-4 text-gray-700">1734m</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:overview.skyraceMaxAlt')}</td>
                      <td className="px-6 py-4 text-gray-700">1863m</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Aid Stations */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2
              {...fadeInUp}
              className="font-futura font-black text-4xl text-primary border-l-8 border-primary pl-6 mb-12"
            >
              {t('regulations:aidStations.title')}
            </motion.h2>

            {/* Mini Skyrace */}
            <motion.div {...fadeInUp} className="mb-16">
              <h3 className="font-futura font-black text-2xl text-gray-900 mb-6">
                {t('regulations:aidStations.miniTitle')}
              </h3>
              <div className="space-y-4">
                {checkpoints.mini.map((cp, index) => (
                  <div
                    key={index}
                    className={`bg-white border-l-4 ${cp.finish ? 'border-primary' : 'border-primary'} rounded-lg p-6 shadow-md`}
                  >
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="bg-primary text-white font-futura font-bold text-sm px-4 py-1 rounded-full">
                        KM {cp.km}
                      </span>
                      <h4 className={`font-futura font-bold text-lg ${cp.finish ? 'text-primary' : 'text-gray-900'} flex-1`}>
                        {cp.finish && `${t('regulations:aidStations.finish')} - `}
                        {cp.location}
                      </h4>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm">
                      {cp.cutoff && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <i className="ri-timer-line text-lg"></i>
                          <span className={cp.finish ? 'font-bold text-red-600' : ''}>
                            {cp.cutoff} {cp.finish && t('regulations:aidStations.finalCutoff')}
                          </span>
                        </div>
                      )}
                      {cp.aid && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <i className="ri-cup-line text-lg"></i>
                          <span>{t('regulations:aidStations.aidStation')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Skyrace */}
            <motion.div {...fadeInUp}>
              <h3 className="font-futura font-black text-2xl text-gray-900 mb-6">
                {t('regulations:aidStations.skyraceTitle')}
              </h3>
              <div className="space-y-4">
                {checkpoints.skyrace.map((cp, index) => (
                  <div
                    key={index}
                    className={`bg-white border-l-4 ${cp.finish ? 'border-primary' : 'border-primary'} rounded-lg p-6 shadow-md`}
                  >
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="bg-primary text-white font-futura font-bold text-sm px-4 py-1 rounded-full">
                        KM {cp.km}
                      </span>
                      <h4 className={`font-futura font-bold text-lg ${cp.finish ? 'text-primary' : 'text-gray-900'} flex-1`}>
                        {cp.finish && `${t('regulations:aidStations.finish')} - `}
                        {cp.location}
                      </h4>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm">
                      {cp.cutoff && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <i className="ri-timer-line text-lg"></i>
                          <span className={cp.finish ? 'font-bold text-red-600' : ''}>
                            {cp.cutoff} {cp.finish && t('regulations:aidStations.finalCutoff')}
                          </span>
                        </div>
                      )}
                      {cp.aid && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <i className="ri-cup-line text-lg"></i>
                          <span>{t('regulations:aidStations.aidStation')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Schedule */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2
              {...fadeInUp}
              className="font-futura font-black text-4xl text-primary border-l-8 border-primary pl-6 mb-12"
            >
              {t('regulations:schedule.title')}
            </motion.h2>

            {/* Friday */}
            <motion.div {...fadeInUp} className="bg-gray-50 rounded-xl p-8 mb-6">
              <h3 className="font-futura font-black text-2xl text-gray-900 mb-6">
                {t('regulations:schedule.friday')}
              </h3>
              <div className="flex items-start gap-4">
                <span className="font-futura font-black text-xl text-primary">15:00 - 21:00</span>
                <div>
                  <p className="text-base text-gray-900 font-semibold">{t('regulations:schedule.bibCollection')}</p>
                  <p className="text-sm text-gray-600 italic">{t('regulations:schedule.locationTBA')}</p>
                </div>
              </div>
            </motion.div>

            {/* Saturday */}
            <motion.div {...fadeInUp} className="bg-white border-4 border-primary rounded-xl p-8">
              <h3 className="font-futura font-black text-2xl text-primary mb-6">
                {t('regulations:schedule.saturday')}
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <span className="font-futura font-black text-xl text-primary min-w-[80px]">06:30</span>
                  <div>
                    <p className="text-base text-gray-900 font-semibold">{t('regulations:schedule.busTransport')}</p>
                    <p className="text-sm text-gray-600">{t('regulations:schedule.reservation')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="font-futura font-black text-xl text-primary min-w-[80px]">07:00 - 07:45</span>
                  <p className="text-base text-gray-900 font-semibold">{t('regulations:schedule.bibAtStart')}</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="font-futura font-black text-2xl text-primary min-w-[80px]">08:00</span>
                  <p className="font-futura font-black text-xl text-gray-900">{t('regulations:schedule.raceStart')}</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="font-futura font-black text-xl text-primary min-w-[80px]">16:00</span>
                  <p className="text-base text-gray-900 font-semibold">{t('regulations:schedule.finalCutoff')}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Registration Info */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2
              {...fadeInUp}
              className="font-futura font-black text-4xl text-primary border-l-8 border-primary pl-6 mb-8"
            >
              {t('regulations:registrationInfo.title')}
            </motion.h2>
            <motion.p {...fadeInUp} className="text-base text-gray-700 mb-8">
              {t('regulations:registrationInfo.method')}
            </motion.p>

            <motion.div {...fadeInUp} className="overflow-x-auto rounded-xl shadow-lg mb-8">
              <table className="w-full">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="px-6 py-4 text-left font-futura font-bold">{t('regulations:registrationInfo.period')}</th>
                    <th className="px-6 py-4 text-left font-futura font-bold">{t('regulations:registrationInfo.skyrace')}</th>
                    <th className="px-6 py-4 text-left font-futura font-bold">{t('regulations:registrationInfo.miniSkyrace')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:registrationInfo.early')}</td>
                    <td className="px-6 py-4 text-primary font-bold">30€</td>
                    <td className="px-6 py-4 text-primary font-bold">15€</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:registrationInfo.standard')}</td>
                    <td className="px-6 py-4 text-gray-700 font-bold">40€</td>
                    <td className="px-6 py-4 text-gray-700 font-bold">20€</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">{t('regulations:registrationInfo.late')}</td>
                    <td className="px-6 py-4 text-gray-700 font-bold">50€</td>
                    <td className="px-6 py-4 text-gray-700 font-bold">25€</td>
                  </tr>
                </tbody>
              </table>
            </motion.div>

            <motion.div {...fadeInUp} className="bg-yellow-50 border-l-4 border-orange-500 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <i className="ri-alert-line text-2xl text-orange-500 mt-0.5"></i>
                <p className="text-base text-gray-900">{t('regulations:registrationInfo.warning')}</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Refund Policy */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2
              {...fadeInUp}
              className="font-futura font-black text-4xl text-primary border-l-8 border-primary pl-6 mb-8"
            >
              {t('regulations:refund.title')}
            </motion.h2>
            <motion.div {...fadeInUp} className="bg-gray-100 rounded-xl p-8">
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-700 text-base leading-relaxed">
                  <i className="ri-checkbox-circle-line text-primary text-xl mt-0.5"></i>
                  <span>{t('regulations:refund.point1')}</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700 text-base leading-relaxed">
                  <i className="ri-checkbox-circle-line text-primary text-xl mt-0.5"></i>
                  <span>{t('regulations:refund.point2')}</span>
                </li>
                <li className="flex items-start gap-3 text-gray-700 text-base leading-relaxed">
                  <i className="ri-checkbox-circle-line text-primary text-xl mt-0.5"></i>
                  <span>{t('regulations:refund.point3')}</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-primary text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.p {...fadeInUp} className="text-xl mb-6">
              {t('regulations:cta.question')}
            </motion.p>
            <motion.a
              {...fadeInUp}
              transition={{ delay: 0.1 }}
              href="mailto:sinjavinaskyrace@gmail.com"
              className="inline-block font-futura font-bold text-2xl mb-8 hover:underline cursor-pointer"
            >
              sinjavinaskyrace@gmail.com
            </motion.a>
            <br />
            <motion.a
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              href="https://www.avaibooksports.com/inscripcion/sinjavina-skyrace/inscripcion_datos/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-primary hover:bg-gray-100 font-futura font-bold text-lg px-12 py-4 rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              {t('regulations:cta.button')}
            </motion.a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}